// filepath: /src/components/Auth/UserMenu.js
import React, { useState, useEffect, useRef } from 'react';
import './UserMenu.css';
import urls from '../../config/urls';

const UserMenu = ({ user, onLogout }) => {
  
  // Función para encriptar datos sensibles
  const encryptData = (data) => {
    try {
      const key = 'thinkhealth_secure_key_2025'; // En producción, usar una clave más segura
      const encrypted = btoa(JSON.stringify(data).split('').map((char, i) => 
        String.fromCharCode(char.charCodeAt(0) ^ key.charCodeAt(i % key.length))
      ).join(''));
      return encrypted;
    } catch (error) {
      console.error('Error al encriptar datos:', error);
      return null;
    }
  };

  // Función para desencriptar datos sensibles
  const decryptData = (encryptedData) => {
    try {
      const key = 'thinkhealth_secure_key_2025';
      const decrypted = atob(encryptedData).split('').map((char, i) => 
        String.fromCharCode(char.charCodeAt(0) ^ key.charCodeAt(i % key.length))
      ).join('');
      return JSON.parse(decrypted);
    } catch (error) {
      console.error('Error al desencriptar datos:', error);
      return {};
    }
  };

  // Función para guardar credenciales de forma segura
  const saveSecureCredentials = (credentials) => {
    try {
      const encrypted = encryptData(credentials);
      if (encrypted) {
        localStorage.setItem('thinkhealth_last_login_secure', encrypted);
        // Limpiar credenciales no encriptadas si existen
        localStorage.removeItem('thinkhealth_last_login');
      }
    } catch (error) {
      console.error('Error al guardar credenciales:', error);
    }
  };
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Migrar credenciales no encriptadas al cargar el componente
  useEffect(() => {
    const migrateOldCredentials = () => {
      try {
        const oldCredentials = localStorage.getItem('thinkhealth_last_login');
        const secureCredentials = localStorage.getItem('thinkhealth_last_login_secure');
        
        // Si hay credenciales antiguas y no hay credenciales seguras
        if (oldCredentials && !secureCredentials) {
          const parsedCredentials = JSON.parse(oldCredentials);
          if (parsedCredentials.username && parsedCredentials.password) {
            saveSecureCredentials(parsedCredentials);
            localStorage.removeItem('thinkhealth_last_login');
            console.log('Credenciales migradas a formato seguro');
          }
        }
      } catch (error) {
        console.error('Error al migrar credenciales:', error);
      }
    };

    migrateOldCredentials();
  }, []);

  // Función para abrir Dashboard y hacer login automático
  const openDashboardWithAutoLogin = () => {
    try {
      // Obtener las credenciales encriptadas del localStorage
      const encryptedCredentials = localStorage.getItem('thinkhealth_last_login_secure');
      const userData = JSON.parse(localStorage.getItem('__amplify__user') || '{}');
      
      if (!encryptedCredentials) {
        console.warn('No hay credenciales guardadas para login automático');
        // Abrir el sistema sin login automático
        window.open(urls.dashboard + '?from=landing', '_blank');
        return;
      }

      // Desencriptar las credenciales
      const savedCredentials = decryptData(encryptedCredentials);
      
      if (!savedCredentials.username || !savedCredentials.password) {
        console.warn('No hay credenciales válidas para login automático');
        // Abrir el sistema sin login automático
        window.open(urls.dashboard + '?from=landing', '_blank');
        return;
      }

      // Abrir nueva pestaña con el sistema
      const systemWindow = window.open(urls.dashboard + '?from=landing', '_blank');

      if (systemWindow) {
        // Esperar a que la ventana cargue y hacer login automático
        setTimeout(() => {
          try {
            // Ejecutar login automático en la nueva ventana
            systemWindow.postMessage({
              type: 'AUTO_LOGIN',
              credentials: {
                username: savedCredentials.username,
                password: savedCredentials.password
              },
              userData: userData
            }, urls.dashboard);
            
          } catch (error) {
            console.log('PostMessage no disponible, login manual requerido');
          }
        }, 2000);

      } else {
        alert('No se pudo abrir la ventana del sistema. Por favor, permite las ventanas emergentes e intenta nuevamente.');
      }
    } catch (error) {
      console.error('Error al abrir dashboard:', error);
      // En caso de error, abrir normalmente
      window.open(urls.dashboard + '?from=landing', '_blank');
    }
  };

  // Función para abrir Configuración con auto-login y redirección INTELIGENTE
  const openConfigurationWithAutoLogin = () => {
    try {
      console.log('🔧 Abriendo configuración con redirección inteligente...');
      
      // Paso 1: Abrir ventana del sistema
      const systemWindow = window.open(urls.dashboard + '?from=landing', '_blank');
      
      if (!systemWindow) {
        alert('No se pudo abrir la ventana del sistema. Por favor, permite las ventanas emergentes e intenta nuevamente.');
        return;
      }

      // Paso 2: Esperar a que cargue y verificar si ya está logueado
      setTimeout(() => {
        try {
          // Enviar mensaje para verificar estado de autenticación
          systemWindow.postMessage({
            type: 'CHECK_AUTH_STATUS',
            requestedRoute: '/admin/configuracion'  // Sin /# al inicio
          }, urls.dashboard);
          
          // Listener para recibir respuesta del sistema
          const handleAuthResponse = (event) => {
            if (event.origin !== new URL(urls.dashboard).origin) {
              return; // Ignorar mensajes de otros orígenes
            }
            
            if (event.data && event.data.type === 'AUTH_STATUS_RESPONSE') {
              const { isAuthenticated, requestedRoute } = event.data;
              
              if (isAuthenticated) {
                console.log('✅ Usuario ya autenticado, redirigiendo directamente a configuración');
                // Usuario ya logueado, redirigir directamente
                systemWindow.postMessage({
                  type: 'DIRECT_REDIRECT',
                  route: requestedRoute
                }, urls.dashboard);
              } else {
                console.log('🔐 Usuario no autenticado, iniciando auto-login...');
                // Usuario no logueado, hacer auto-login y luego redirigir
                
                const encryptedCredentials = localStorage.getItem('thinkhealth_last_login_secure');
                if (!encryptedCredentials) {
                  console.warn('No hay credenciales para auto-login, el usuario debe loguearse manualmente');
                  return;
                }
                
                const savedCredentials = decryptData(encryptedCredentials);
                if (!savedCredentials.username || !savedCredentials.password) {
                  console.warn('Credenciales inválidas para auto-login');
                  return;
                }
                
                const userData = JSON.parse(localStorage.getItem('__amplify__user') || '{}');
                
                // Enviar credenciales para auto-login con redirección
                systemWindow.postMessage({
                  type: 'AUTO_LOGIN',
                  credentials: {
                    username: savedCredentials.username,
                    password: savedCredentials.password
                  },
                  userData: userData,
                  redirectTo: requestedRoute
                }, urls.dashboard);
              }
              
              // Remover listener una vez procesado
              window.removeEventListener('message', handleAuthResponse);
            }
          };
          
          // Agregar listener temporal para la respuesta
          window.addEventListener('message', handleAuthResponse);
          
          // Timeout de seguridad: remover listener después de 10 segundos
          setTimeout(() => {
            window.removeEventListener('message', handleAuthResponse);
          }, 10000);
          
        } catch (error) {
          console.error('Error en verificación de autenticación:', error);
        }
      }, 2000); // Esperar 2 segundos para que cargue la ventana

    } catch (error) {
      console.error('Error al abrir configuración:', error);
      // Fallback: abrir directamente la URL de configuración
      window.open(urls.configuration, '_blank');
    }
  };

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        buttonRef.current && 
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cerrar menú con tecla Escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Ajustar posición del dropdown en móvil
  useEffect(() => {
    const adjustDropdownPosition = () => {
      if (isOpen && menuRef.current) {
        const dropdown = menuRef.current;
        const rect = dropdown.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        
        // Resetear transform antes de calcular
        dropdown.style.transform = '';
        const newRect = dropdown.getBoundingClientRect();
        
        // Si el dropdown se sale por la derecha
        if (newRect.right > viewportWidth - 10) {
          const adjustment = newRect.right - viewportWidth + 20;
          dropdown.style.transform = `translateX(-${adjustment}px)`;
        }
        // Si se sale por la izquierda
        else if (newRect.left < 10) {
          dropdown.style.transform = `translateX(${10 - newRect.left}px)`;
        }
      }
    };

    adjustDropdownPosition();
    
    // Agregar listener para resize
    window.addEventListener('resize', adjustDropdownPosition);
    return () => window.removeEventListener('resize', adjustDropdownPosition);
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleMenuItemClick = (action) => {
    setIsOpen(false);
    if (action === 'logout') {
      onLogout();
    }
    if (action === 'dashboard') {
      openDashboardWithAutoLogin();
    }
    if (action === 'settings') {
      openConfigurationWithAutoLogin();
    }
    if (action === 'help') {
      // Scroll to contacts section
      const contactSection = document.getElementById('contacto');
      if (contactSection) {
        contactSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
    // Aquí puedes agregar más acciones según las opciones que necesites
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const menuItems = [
    {
      id: 'profile',
      label: 'Mi Perfil',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 8a3 3 0 100-6 3 3 0 000 6zM8 10c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
        </svg>
      ),
      action: 'profile'
    },
    {
      id: 'settings',
      label: 'Configuración',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 10.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M6.5 2.34A6 6 0 019.5 2.34l.84 1.45A6 6 0 0112.66 5.5l1.45.84A6 6 0 0114.11 9.5l-1.45.84A6 6 0 0110.34 12.66l-.84 1.45A6 6 0 016.5 14.11l-.84-1.45A6 6 0 013.34 10.5L1.89 9.66A6 6 0 011.89 6.34l1.45-.84A6 6 0 015.66 3.34L6.5 2.34z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        </svg>
      ),
      action: 'settings'
    },
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M2 4.5A2.5 2.5 0 014.5 2h7A2.5 2.5 0 0114 4.5v7a2.5 2.5 0 01-2.5 2.5h-7A2.5 2.5 0 012 11.5v-7z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M5 6h6M5 8h4M5 10h3" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      ),
      action: 'dashboard'
    },
    {
      id: 'help',
      label: 'Ayuda',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M6 6.5A2 2 0 018 5c.8 0 1.5.4 1.8 1M8 11h.01M8 8.5v1" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
        </svg>
      ),
      action: 'help'
    },
    {
      id: 'divider',
      type: 'divider'
    },
    {
      id: 'logout',
      label: 'Cerrar Sesión',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 2h3a1 1 0 011 1v10a1 1 0 01-1 1h-3M6 12l4-4-4-4M10 8H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </svg>
      ),
      action: 'logout',
      danger: true
    }
  ];

  return (
    <div className="user-menu-container">
      {/* User Button */}
      <button
        ref={buttonRef}
        className={`user-menu-button ${isOpen ? 'active' : ''}`}
        onClick={toggleMenu}
        aria-label={`Menú de usuario: ${user.name}`}
        aria-expanded={isOpen}
      >
        <div className="user-avatar">
          {user.avatar ? (
            <img src={user.avatar} alt={user.name} />
          ) : (
            <div className="user-avatar-initials">
              {getInitials(user.name)}
            </div>
          )}
        </div>
        <div className="user-info">
          <span className="user-name">{user.name}</span>
          <span className="user-role">{user.role === 'admin' ? 'Administrador' : 'Usuario'}</span>
        </div>
        <div className="user-menu-chevron">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div ref={menuRef} className="user-dropdown-menu">
          <div className="user-dropdown-header">
            <div className="user-dropdown-avatar">
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} />
              ) : (
                <div className="user-dropdown-avatar-initials">
                  {getInitials(user.name)}
                </div>
              )}
            </div>
            <div className="user-dropdown-info">
              <div className="user-dropdown-name">{user.name}</div>
              <div className="user-dropdown-username">@{user.username}</div>
            </div>
          </div>

          <div className="user-dropdown-items">
            {menuItems.map((item) => {
              if (item.type === 'divider') {
                return <div key={item.id} className="user-dropdown-divider" />;
              }

              return (
                <button
                  key={item.id}
                  className={`user-dropdown-item ${item.danger ? 'danger' : ''}`}
                  onClick={() => handleMenuItemClick(item.action)}
                >
                  <span className="user-dropdown-item-icon">
                    {item.icon}
                  </span>
                  <span className="user-dropdown-item-label">
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
